import React, { FormEvent, useRef, useState } from "react";
import { shadowsVal } from "@utils/constants/style";
import { theme, Input } from "nectar-ui";
import AutosuggestHighlightMatch from "autosuggest-highlight/match";
import AutosuggestHighlightParse from "autosuggest-highlight/parse";
import Autosuggest, {
  Theme as AutosuggestTheme,
  GetSuggestionValue,
  RenderInputComponentProps,
  OnSuggestionSelected,
} from "react-autosuggest";
import { Suggestion } from "@customTypes/autosuggest";

const themeStyle: AutosuggestTheme = {
  container: {
    position: "relative",
    width: "100%",
  },
  suggestionsList: {
    position: "absolute",
    top: "100%",
    width: "100%",
    left: 0,
    listStyle: "none",
    padding: 0,
    backgroundColor: theme.standard.colors.white,
    boxShadow: shadowsVal.xs,
    borderRadius: `0 0 ${theme.standard.borderRadius.sm} ${theme.standard.borderRadius.sm}`,
    maxHeight: 200,
    overflow: "auto",
    zIndex: 5,
  },
  suggestion: {
    padding: "8px 10px",
    cursor: "pointer",
    fontSize: 12,
  },
  suggestionHighlighted: {
    backgroundColor: theme.standard.colors.gray100,
  },
};

const renderInputComponent = (inputProps: RenderInputComponentProps) => (
  <>
    <Input {...inputProps} icon="fa-search" />
  </>
);

function escapeRegexCharacters(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const getSuggestionValue: GetSuggestionValue<Suggestion> = (suggestion) => {
  return suggestion.name;
};

function getSuggestions(suggestions: Suggestion[], value: string) {
  const escapedValue = escapeRegexCharacters(value.trim());
  if (escapedValue === "") return [];
  const regex = new RegExp(`\\b${escapedValue}`, "i");
  return suggestions.filter((item) => regex.test(item.name));
}

const renderSuggestion = (suggestion: Suggestion, { query }: { query: string }) => {
  const suggestionText = `${suggestion.name}`;
  const matches = AutosuggestHighlightMatch(suggestionText, query);
  const parts = AutosuggestHighlightParse(suggestionText, matches);

  const spanArray = parts.map((part, index) => {
    const fontWeight = part.highlight ? "bold" : "lighter";
    return (
      <span key={`${part}-${index}`} style={{ fontWeight }}>
        {part.text}
      </span>
    );
  });

  return spanArray;
};

type AutoSuggestProps = {
  suggestionList: Suggestion[];
  inputSize?: number;
  placeholder: string;
  onSelect: (suggestion: Suggestion) => void;
  onChange: (value: string) => void;
  debounceTime?: number;
  disabled: boolean;
};

const AutoSuggest: React.FC<AutoSuggestProps> = ({
  suggestionList = [],
  inputSize,
  placeholder,
  onSelect,
  onChange,
  debounceTime = 1000,
  disabled,
}) => {
  const [value, setValue] = useState("");
  const timer = useRef<NodeJS.Timeout>();

  const handleChange = (event: FormEvent<HTMLElement>, { newValue }: { newValue: string }) => {
    setValue(newValue);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      onChange(newValue);
    }, debounceTime);
  };

  const onSuggestionsFetchRequested = ({ value: newValue }: { value: string }) => {
    getSuggestions(suggestionList, newValue);
  };

  const onSuggestionsClearRequested = () => {
    setValue("");
  };

  const onSuggestionSelected: OnSuggestionSelected<Suggestion> = (
    event: FormEvent<HTMLElement>,
    { suggestion }
  ) => {
    setValue("");
    onSelect(suggestion);
  };

  const inputProps = {
    placeholder,
    value,
    onChange: handleChange,
    size: inputSize,
    disabled,
  };

  return (
    <Autosuggest
      theme={themeStyle}
      suggestions={suggestionList}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      onSuggestionsClearRequested={onSuggestionsClearRequested}
      getSuggestionValue={getSuggestionValue}
      renderSuggestion={renderSuggestion}
      renderInputComponent={renderInputComponent}
      inputProps={inputProps}
      onSuggestionSelected={onSuggestionSelected}
      focusInputOnSuggestionClick={false}
      highlightFirstSuggestion
    />
  );
};

export default AutoSuggest;
